/**
 * Sidebar Navigation Component (v3.0)
 *
 * Oriented around Operating Domains (conditions-met, not phase gates):
 *
 * AWARENESS & RESPONSIBILITIES
 * - Dashboard (strategic awareness, what matters now)
 * - Tasks (assigned tasks, created tasks)
 *
 * ITEM MANAGEMENT (always-on domain)
 * - Intake (idea submission, triage)
 * - Items (inventory: item readiness, altitude, clusters, value exploration)
 *
 * STRATEGY
 * - Strategies hub (frameworks + relationship map), classic alignment (objectives, goals),
 *   Preliminary Roadmaps (/planning/roadmap),
 *   clarity artifacts (open questions, outcome drivers, desired outcomes, back plans),
 *   OST, kernel, strategy canvas
 *
 * CYCLES & EXECUTION
 * - Cycles (cycle list/detail, tracks, sessions, session results, priority lists, the Cascade; templates/workflows live under Settings)
 * - Item Pools (comparison sets) under Item Management
 *
 * ADMIN
 * - Settings (collapsed by default, admin only)
 *
 * Navigation items are filtered based on the current user's role permissions.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  User,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { TenantSwitcher } from './TenantSwitcher';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useSavedViewsStore } from '../../stores/prioritization/savedViewsStore';
import { displayViewName, itemsViewPath } from '../../types/prioritization/savedViews';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import { PrioritiesLogo } from '../ui/PrioritiesLogo';
import { useSyncConflictBadgeCount } from '../../hooks/useSyncRealtime';
import { useAmbientRuntimeHealth } from '../../hooks/useAmbientRuntimeHealth';
import { SpaceSwitcher } from './SpaceSwitcher';
import { ProductModeSwitcher } from './ProductModeSwitcher';
import { setSettingsEntryProduct } from '../../lib/productSurfacePaths';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { monoIconButton } from '@/components/mono/iconButton';
import { useSidebarNavigation } from './useSidebarNavigation';

/** Human-readable label for role name (slug or display name). */
function roleLabel(name: string): string {
  const n = name.toLowerCase().replace(/-/g, ' ');
  if (n === 'workspace admin') return 'Workspace Admin';
  if (n === 'tenant admin') return 'Tenant Admin';
  if (n === 'platform admin' || n === 'system admin') return 'Platform Admin';
  return name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentRole = useUserStore((state) => state.currentRole);
  const assignedRoleNames = useUserStore((state) => state.assignedRoleNames);
  const permissionsLoaded = useUserStore((state) => state.permissionsLoaded);
  const userName = useUserStore((state) => state.userName);
  // The menu itself — sections, gates and the area's composition — is shared
  // with the phone frame (useSidebarNavigation).
  const { allNavigation, areaNavigation } = useSidebarNavigation();

  // Live conflict badge count from Supabase Realtime (Phase 3)
  const syncConflictCount = useSyncConflictBadgeCount();

  // Ambient runtime health. Polled rather than Realtime on purpose: a stalled
  // scheduler is the ABSENCE of events, and no subscription fires on nothing
  // happening — which is exactly how it stayed invisible for seven weeks.
  // Costs nothing for workspaces without the ambient_runtime module (the hook
  // issues no query at all).
  const ambientHealth = useAmbientRuntimeHealth();

  const pinnedCycleId = usePinnedCycleStore(state => state.pinnedCycleId);
  const pinnedCycleName = usePinnedCycleStore(state => state.pinnedCycleName);
  const exitWorkspace = usePinnedCycleStore(state => state.exitWorkspace);
  const pinnedTrackId = usePinnedCycleStore(state => state.pinnedTrackId);
  const pinnedTrackName = usePinnedCycleStore(state => state.pinnedTrackName);
  const exitTrack = usePinnedCycleStore(state => state.exitTrack);
  const pinnedSessionId = usePinnedCycleStore(state => state.pinnedSessionId);
  const pinnedSessionName = usePinnedCycleStore(state => state.pinnedSessionName);
  const exitSession = usePinnedCycleStore(state => state.exitSession);

  // Track which sections are expanded - start collapsed
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  // Track which nav sub-groups (items with subItems) are expanded
  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(new Set());
  const toggleSubGroup = (name: string) => {
    setExpandedSubGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); } else { next.add(name); }
      return next;
    });
  };
  
  // Auto-expand section based on current route (v3.0 - design vs execution split)
  const currentSectionId = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/intake')) return 'intake';
    if (path.includes('/relationships')) return 'relationships';
    // /collections (bare — not cycle-design) lives under both Relationships and Items; Relationships wins when expanded
    if (path.includes('/items') || path.includes('/shape') || path.includes('/scoring') || path.includes('/leveling') || path.includes('/lineage') || path.includes('/input-rounds') || path.includes('/needs-definition') || path.includes('/needs-defining') || path.includes('/missing-metadata') || path.includes('/lists') || (path.includes('/collections') && !path.includes('/cycle-design'))) return 'items';
    // Roadmaps (both strategic and horizon/product) live under Strategy
    if (path.includes('/prioritization/roadmap') || path.includes('/prioritization/product-roadmap')) return 'strategy';
    if (path.includes('/open-questions') || path.includes('/backplan') || path.includes('/outcome-drivers') || path.includes('/desired-outcomes')) return 'strategy';
    if (path.includes('/strategy')) return 'strategy';
    // Session Results lives under Cycles in nav; match before generic /reports → monitor
    if (path.includes('/reports/sessions')) return 'cycle-execution';
    if (path.includes('/decisions') || path.includes('/decision-intelligence') || path.includes('/prioritization/calibration') || path.includes('/insights/cognitive-bias') || path.includes('/insights/judgment-index') || path.includes('/insights/judgment-analytics')) return 'decisions';
    if (path.includes('/reports') || path.includes('/monitor')) return 'monitor';
    // Cycles workspace: hub, cycle list/detail, tracks, sessions, catchball, governance, published lists
    if (path.includes('/cycle-design')) return 'cycle-execution';
    if (path.includes('/scoreboard')) return 'cycle-execution';
    if (
      path.includes('/tracks') ||
      path.includes('/sessions') ||
      path.includes('/catchball') ||
      path.includes('/tournaments') ||
      path.includes('/governance')
    ) return 'cycle-execution';
    if (path.includes('/cycles/')) return 'cycle-execution';
    if (path.includes('/cycles')) return 'cycle-execution';
    // Avoid matching `/prioritization/plan-of-record` via a naive `/plans` substring check.
    if (path.includes('/prioritization/plan-of-record')) return 'home';
    if (path.includes('/priorities') || path.includes('/prioritization/plans')) return 'cycle-execution';
    if (path.includes('/tools') || path.includes('/exercises')) return 'tools';
    if (path.includes('/settings') || path.includes('/admin')) return 'settings';
    return null;
  }, [location.pathname]);
  
  // Auto-expand the active section when route changes.
  // For settings: only expand the specific group whose item hrefs match the current path.
  useEffect(() => {
    if (!currentSectionId) return;
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (currentSectionId === 'settings') {
        // Only expand the settings group that owns the current path
        allNavigation.forEach(s => {
          if (s.sectionId !== 'settings') return;
          const hasMatch =
            (s.headerHref && location.pathname.startsWith(s.headerHref)) ||
            s.items?.some(item => !item.isDivider && location.pathname.startsWith(item.href));
          if (hasMatch) newSet.add(s.name);
        });
      } else {
        allNavigation.forEach(s => {
          if (s.sectionId === currentSectionId) newSet.add(s.name);
        });
        if (!newSet.has(currentSectionId)) newSet.add(currentSectionId);
      }
      return newSet;
    });
  }, [currentSectionId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const toggleSection = (sectionName: string, navHref?: string) => {
    const wasExpanded = expandedSections.has(sectionName);

    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (wasExpanded) { newSet.delete(sectionName); } else { newSet.add(sectionName); }
      return newSet;
    });

    // Only navigate when expanding — navigating on collapse would re-trigger the
    // auto-expand effect, causing the section to immediately re-open (double-click bug).
    if (!wasExpanded && navHref) {
      navigate(navHref);
    }
  };
  
  /**
   * Returns true when the current pathname matches `href`.
   *
   * When `sectionOwner` is provided the result is additionally gated on whether
   * `currentSectionId` agrees that the given section is the canonical owner of
   * this path.  This prevents dual-highlighting when the same URL appears as a
   * nav item in two different sections (e.g. /scoring in Items and another group).
   *
   * Falls back to a plain path-match when:
   *   - no sectionOwner is given (legacy call-sites)
   *   - currentSectionId is null (we are on an untracked page)
   */
  const isActive = (href: string, sectionOwner?: string | null) => {
    const [hrefPath, hrefQuery] = href.split('?');
    const pathMatch =
      location.pathname === hrefPath ||
      (hrefPath !== '/prioritization' && location.pathname.startsWith(hrefPath));

    if (!pathMatch) return false;

    // If the href includes a query string, require an exact full match.
    if (hrefQuery && `${location.pathname}${location.search}` !== href) return false;

    if (!sectionOwner || !currentSectionId) return true;

    // Only highlight this item if THIS section is the one routing owns right now.
    return sectionOwner === currentSectionId;
  };


  // Get pinned saved views - select raw views and filter in useMemo to avoid infinite loop
  const allSavedViews = useSavedViewsStore(state => state.views);
  const pinnedViews = useMemo(() => {
    return allSavedViews.filter(v => v.isPinned && v.status === 'active');
  }, [allSavedViews]);
  
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const collapsed = !sidebarOpen;

  return (
    <div
      data-collapsed={collapsed || undefined}
      className={`pp-sidebar fixed inset-y-0 left-0 z-50 border-r flex flex-col transition-[width,colors] duration-200 ${
        collapsed ? 'w-16' : 'w-64'
      } bg-[var(--bg-raised)] border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:border-[var(--rule)]`}
    >
      {/* Logo — height matches top bar row (h-14) for alignment with main header icons */}
      <div
        className={`flex h-14 shrink-0 items-center border-b border-[var(--rule)] dark:border-[var(--rule)] ${
          collapsed ? 'justify-center px-1' : 'justify-between px-3'
        }`}
      >
        <Link
          to="/prioritization"
          className="flex items-center leading-none"
          title={collapsed ? 'Priorities home' : undefined}
        >
          <PrioritiesLogo height={28} markOnly={collapsed} />
        </Link>
        {!collapsed && (
          <button
            type="button"
            onClick={() => toggleSidebar()}
            className={monoIconButton('sm')}
            title="Minimize navigation"
            aria-label="Minimize navigation"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Priorities / Mandate / Plans product modes */}
      <ProductModeSwitcher collapsed={collapsed} />

      {/* Navigation — when collapsed, hide labels via descendant selectors */}
      <nav
        className={`flex-1 overflow-y-auto py-4 ${
          collapsed
            ? 'px-1.5 space-y-0.5 [&_span]:sr-only [&_button]:justify-center [&_a]:justify-center [&_a]:gap-0 [&_a]:px-0 [&_button]:gap-0 [&_button]:px-0 [&_.h-3]:hidden [&_.h-3\\.5]:hidden'
            : 'px-3'
        }`}
      >
        {/* Pinned Views */}
        {pinnedViews.length > 0 && !collapsed && (
          <div className="mb-4 pb-4 border-b border-[var(--rule)]">
            <div className="px-3 mb-2 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
              Pinned Views
            </div>
            <div className="space-y-0.5">
              {pinnedViews.map((view) => {
                const viewPath = `/prioritization/${itemsViewPath(view.entityType)}?view=${view.id}`;
                const active = location.search.includes(`view=${view.id}`);
                return (
                  <Link
                    key={view.id}
                    to={viewPath}
                    className={`group flex items-center gap-3 px-3 py-2.5  text-sm font-medium transition-all ${
                      active
                        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                        : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    <MapPin className={`h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110 ${active ? 'text-[var(--mustard)] fill-amber-100' : 'text-[var(--text-ter)]'}`} />
                    <span className="truncate">{displayViewName(view.name)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="space-y-1">
          {areaNavigation.map((section, index) => {
            const showGroupLabel = section.groupLabel && (
              index === 0 || areaNavigation[index - 1]?.groupLabel !== section.groupLabel
            );

            return (
              <div key={section.sectionId + '-' + section.name + (section.href ?? '')}>
                {showGroupLabel && !collapsed && (
                  <div className="px-3 pt-4 pb-1.5">
                    {/* Cartographic diamond rule — map legend divider */}
                    <div className="flex items-center gap-2 mb-1.5" aria-hidden="true">
                      <div className="h-px flex-1 bg-[var(--hover-tint)]" />
                      <div className="w-1 h-1 rotate-45 bg-[var(--rule-bright)] flex-shrink-0" />
                      <div className="h-px flex-1 bg-[var(--hover-tint)]" />
                    </div>
                    <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                      {section.groupLabel}
                    </span>
                  </div>
                )}
                {(() => {
            // Flat section — non-clickable label + items shown at root level, no collapsible
            if (section.flatSection && section.items) {
              return (
                <div className="space-y-0.5">
                  {!collapsed && (
                  <div className="px-3 pt-3 pb-1">
                    <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                      {section.name}
                    </span>
                  </div>
                  )}
                  {/* Focus mode breadcrumb chain (cycle-execution only) */}
                  {section.sectionId === 'cycle-execution' && section.name === 'Cycles' && pinnedCycleId && !collapsed && (
                    <div className="space-y-1 mb-2 px-1">
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)] flex-shrink-0" />
                        <span className="text-[10px] text-[var(--mustard)] font-bold uppercase tracking-wide flex-shrink-0">Cycle</span>
                        <span className="text-xs text-[var(--mustard)] font-medium truncate flex-1" title={pinnedCycleName ?? undefined}>
                          {pinnedCycleName ?? '—'}
                        </span>
                        <button type="button" onClick={exitWorkspace} title="Clear active cycle"
                          className="text-[var(--mustard)] hover:text-[var(--mustard)] transition-colors flex-shrink-0">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      {/* Words on the slate wash are --text-sec: --text-ter is 4.45:1 there. */}
                      {pinnedTrackId && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 ml-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
                          <span className="text-[10px] text-[var(--text-sec)] font-bold uppercase tracking-wide flex-shrink-0">Track</span>
                          <span className="text-xs text-[var(--text-sec)] font-medium truncate flex-1" title={pinnedTrackName ?? undefined}>
                            {pinnedTrackName ?? '—'}
                          </span>
                          <button type="button" onClick={exitTrack} title="Clear active Track"
                            className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {pinnedSessionId && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 ml-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
                          <span className="text-[10px] text-[var(--text-sec)] font-bold uppercase tracking-wide flex-shrink-0">Session</span>
                          <span className="text-xs text-[var(--text-sec)] font-medium truncate flex-1" title={pinnedSessionName ?? undefined}>
                            {pinnedSessionName ?? '—'}
                          </span>
                          <button type="button" onClick={exitSession} title="Clear active session"
                            className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {section.items.map((item) => {
                    const active = isActive(item.href, section.sectionId);
                    if (item.isSubItem) {
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          state={item.linkState}
                          title={collapsed ? item.name : undefined}
                          aria-label={item.name}
                          className={`flex items-center  text-sm transition-all ${
                            collapsed
                              ? 'justify-center h-10 w-10 mx-auto'
                              : 'gap-2.5 pl-7 pr-3 py-1.5'
                          } ${
                            active
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-[var(--text-ter)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                          }`}
                        >
                          <item.icon className={`flex-shrink-0 ${collapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} ${active ? 'text-primary-500' : 'text-[var(--text-ter)]'}`} />
                          {!collapsed && <span>{item.name}</span>}
                          {!collapsed && item.badge !== undefined && item.badge > 0 && (
                            <span className="ml-auto bg-[var(--coral-dim)] text-[var(--coral-text)] text-xs font-medium px-2 py-0.5 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    }
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        state={item.linkState}
                        title={collapsed ? item.name : undefined}
                        aria-label={item.name}
                        className={`flex items-center  text-sm font-medium transition-all ${
                          collapsed
                            ? 'justify-center h-10 w-10 mx-auto'
                            : 'gap-3 px-3 py-2.5'
                        } ${
                          active ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                        }`}
                      >
                        <item.icon className={`h-5 w-5 ${active ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                        {!collapsed && <span className="flex-1">{item.name}</span>}
                        {!collapsed && item.badge !== undefined && item.badge > 0 && (
                          <span className="ml-auto bg-[var(--coral-dim)] text-[var(--coral-text)] text-xs font-medium px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              );
            }

            // Single item (no children)
            if (section.href && !section.items) {
              const active = isActive(section.href, section.sectionId);
              return (
                  <Link
                  key={section.name}
                  to={section.href}
                  title={collapsed ? section.name : undefined}
                  aria-label={section.name}
                  onClick={() => {
                    if (section.sectionId === 'settings') {
                      setSettingsEntryProduct('prioritization');
                    }
                  }}
                  className={`flex items-center  text-sm font-medium transition-all ${
                    collapsed
                      ? 'justify-center h-10 w-10 mx-auto'
                      : 'gap-3 px-3 py-2.5'
                  } ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  <section.icon className={`h-5 w-5 ${active ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                  {!collapsed && <span className="flex-1">{section.name}</span>}
                  {!collapsed && section.badge !== undefined && section.badge > 0 && (
                    <span className="ml-auto bg-[var(--coral-dim)] text-[var(--coral-text)] text-xs font-medium px-2 py-0.5 rounded-full">
                      {section.badge}
                    </span>
                  )}
                </Link>
              );
            }
            
            // Section with children
            const isExpanded = expandedSections.has(section.name);
            const hasActiveChild = section.activePaths
              ? section.activePaths.some(p => location.pathname.startsWith(p)) &&
                !section.activePathExclusions?.some(p => location.pathname.startsWith(p))
              : (section.headerHref && location.pathname.startsWith(section.headerHref)) ||
                section.items?.some(item => !item.isDivider && isActive(item.href));

            return (
              <div key={section.sectionId + '-' + section.name} className="space-y-0.5">
                {/* Section header */}
                <button
                  type="button"
                  onClick={() => toggleSection(section.name, section.headerHref ?? section.items?.[0]?.href)}
                  title={collapsed ? section.name : undefined}
                  aria-label={section.name}
                  aria-expanded={isExpanded}
                  className={`w-full flex items-center  text-sm font-medium transition-all ${
                    collapsed
                      ? 'justify-center h-10 w-10 mx-auto px-0'
                      : 'gap-3 px-3 py-2.5'
                  } ${
                    hasActiveChild
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  <section.icon className={`h-5 w-5 ${hasActiveChild ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                  {!collapsed && <span className="flex-1 text-left">{section.name}</span>}
                  {/* Focus mode dot — expanded sidebar only */}
                  {!collapsed && section.sectionId === 'cycle-execution' && (pinnedCycleId || pinnedTrackId || pinnedSessionId) && (
                    <span
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${
                        pinnedSessionId ? 'bg-[var(--slate)]' : pinnedTrackId ? 'bg-[var(--slate)]' : 'bg-[var(--mustard)]'
                      }`}
                      title={pinnedSessionId ? 'Session mode active' : pinnedTrackId ? 'Track mode active' : 'Cycle mode active'}
                    />
                  )}
                  {/* Sync conflict badge — expanded sidebar only */}
                  {!collapsed && section.name === 'Integrations & Sync' && syncConflictCount > 0 && (
                    <span
                      className="h-5 min-w-5 px-1 rounded-full bg-[var(--coral)] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                      title={`${syncConflictCount} sync conflict${syncConflictCount !== 1 ? 's' : ''} need review`}
                    >
                      {syncConflictCount > 99 ? '99+' : syncConflictCount}
                    </span>
                  )}
                  {/* Ambient runtime health badge — same section, same shape as
                      the conflict badge. `loaded` is checked so a slow first
                      fetch never flashes a false all-clear or a false alarm. */}
                  {!collapsed &&
                    section.name === 'Integrations & Sync' &&
                    ambientHealth.loaded &&
                    ambientHealth.needsAttentionCount > 0 && (
                      <span
                        className="h-5 min-w-5 px-1 rounded-full bg-[var(--mustard)] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                        title={
                          ambientHealth.headline ??
                          `${ambientHealth.needsAttentionCount} ambient job(s) need attention`
                        }
                      >
                        {ambientHealth.needsAttentionCount > 99
                          ? '99+'
                          : ambientHealth.needsAttentionCount}
                      </span>
                    )}
                  {!collapsed && (isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
                  ))}
                </button>
                
                {/* Section items — flat centered rail when collapsed */}
                {isExpanded && section.items && (
                  <div
                    className={
                      collapsed
                        ? 'space-y-0.5'
                        : 'ml-5 pl-3 border-l border-[var(--rule)] space-y-0.5'
                    }
                  >
                    {/* Progressive focus mode indicators — expanded sidebar only */}
                    {!collapsed && section.sectionId === 'cycle-execution' && pinnedCycleId && (
                      <div className="space-y-1 mb-2">
                        {/* Cycle */}
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)] flex-shrink-0" />
                          <span className="text-[10px] text-[var(--mustard)] font-bold uppercase tracking-wide flex-shrink-0">Cycle</span>
                          <span className="text-xs text-[var(--mustard)] font-medium truncate flex-1" title={pinnedCycleName ?? undefined}>
                            {pinnedCycleName ?? '—'}
                          </span>
                          <button type="button" onClick={exitWorkspace} title="Clear active cycle"
                            className="text-[var(--mustard)] hover:text-[var(--mustard)] transition-colors flex-shrink-0">
                            <X className="h-3 w-3" />
                          </button>
                        </div>

                        {/* Track — shown when pinnedTrackId is set */}
                        {pinnedTrackId && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 ml-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
                            <span className="text-[10px] text-[var(--text-sec)] font-bold uppercase tracking-wide flex-shrink-0">Track</span>
                            <span className="text-xs text-[var(--text-sec)] font-medium truncate flex-1" title={pinnedTrackName ?? undefined}>
                              {pinnedTrackName ?? '—'}
                            </span>
                            <button type="button" onClick={exitTrack} title="Clear active Track"
                              className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}

                        {/* Session — shown when pinnedSessionId is set */}
                        {pinnedSessionId && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 ml-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
                            <span className="text-[10px] text-[var(--text-sec)] font-bold uppercase tracking-wide flex-shrink-0">Session</span>
                            <span className="text-xs text-[var(--text-sec)] font-medium truncate flex-1" title={pinnedSessionName ?? undefined}>
                              {pinnedSessionName ?? '—'}
                            </span>
                            <button type="button" onClick={exitSession} title="Clear active session"
                              className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {section.items.map((item) => {
                      if (item.isDivider) {
                        if (collapsed) return null;
                        return (
                          <div key={item.dividerLabel ?? item.name} className="px-3 pt-3 pb-1">
                            <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">{item.dividerLabel ?? item.name}</span>
                          </div>
                        );
                      }

                      // Item with nested sub-items — renders as an expandable group
                      if (item.subItems && item.subItems.length > 0) {
                        const subExpanded = expandedSubGroups.has(item.name);
                        const hasActiveSub = item.subItems.some(s => isActive(s.href, section.sectionId));
                        return (
                          <div key={item.name} className="space-y-0.5">
                            <button
                              type="button"
                              onClick={() => toggleSubGroup(item.name)}
                              title={collapsed ? item.name : undefined}
                              aria-label={item.name}
                              aria-expanded={subExpanded}
                              className={`w-full flex items-center  text-sm transition-all ${
                                collapsed
                                  ? 'justify-center h-10 w-10 mx-auto px-0'
                                  : 'gap-3 px-3 py-2.5'
                              } ${
                                hasActiveSub
                                  ? 'bg-primary-50 text-primary-700 font-medium'
                                  : 'text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                              }`}
                            >
                              <item.icon className={`${collapsed ? 'h-5 w-5' : 'h-4 w-4'} ${hasActiveSub ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                              {!collapsed && <span className="flex-1 text-left">{item.name}</span>}
                              {!collapsed && (subExpanded
                                ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                                : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                              )}
                            </button>
                            {subExpanded && (
                              <div
                                className={
                                  collapsed
                                    ? 'space-y-0.5'
                                    : 'ml-4 pl-3 border-l border-[var(--rule)] space-y-0.5'
                                }
                              >
                                {item.subItems.map((sub) => {
                                  const subActive = isActive(sub.href, section.sectionId);
                                  return (
                                    <Link
                                      key={sub.href}
                                      to={sub.href}
                                      state={sub.linkState}
                                      title={collapsed ? sub.name : undefined}
                                      aria-label={sub.name}
                                      className={`flex items-center  text-sm transition-all ${
                                        collapsed
                                          ? 'justify-center h-10 w-10 mx-auto'
                                          : 'gap-3 px-3 py-1.5'
                                      } ${
                                        subActive
                                          ? 'bg-primary-50 text-primary-700 font-medium'
                                          : 'text-[var(--text-ter)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                                      }`}
                                    >
                                      <sub.icon className={`${collapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} ${subActive ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                                      {!collapsed && <span>{sub.name}</span>}
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }

                      const active = isActive(item.href, section.sectionId);

                      // Indented sub-item (no expand/collapse, always visible)
                      if (item.isSubItem) {
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            state={item.linkState}
                            title={collapsed ? item.name : undefined}
                            aria-label={item.name}
                            className={`flex items-center  text-sm transition-all ${
                              collapsed
                                ? 'justify-center h-10 w-10 mx-auto'
                                : 'gap-2.5 pl-7 pr-3 py-1.5'
                            } ${
                              active
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-[var(--text-ter)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                            }`}
                          >
                            <item.icon className={`flex-shrink-0 ${collapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} ${active ? 'text-primary-500' : 'text-gray-350'}`} />
                            {!collapsed && <span>{item.name}</span>}
                          </Link>
                        );
                      }

                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          state={item.linkState}
                          title={collapsed ? item.name : undefined}
                          aria-label={item.name}
                          className={`flex items-center  text-sm transition-all ${
                            collapsed
                              ? 'justify-center h-10 w-10 mx-auto'
                              : 'gap-3 px-3 py-2.5'
                          } ${
                            active
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                          }`}
                        >
                          <item.icon className={`${collapsed ? 'h-5 w-5' : 'h-4 w-4'} ${active ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
                          {!collapsed && <span>{item.name}</span>}
                          {!collapsed && item.badge !== undefined && (
                            <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
                              item.href?.includes('/triage') || item.href?.includes('/needs-definition')
                                ? 'bg-[var(--coral-dim)] text-[var(--coral-text)]'
                                : 'bg-primary-100 text-primary-700'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
                })()}
              </div>
            );
          })}
        </div>
      </nav>
      
      {/* Workspace + account — fixed strip below scrollable nav */}
      <div
        className={`flex-shrink-0 border-t border-[var(--rule)] dark:border-[var(--rule)] ${
          collapsed ? 'p-2 flex flex-col items-center space-y-2' : 'p-4 space-y-3'
        }`}
      >
        <TenantSwitcher compact iconOnly={collapsed} />

        <SpaceSwitcher iconOnly={collapsed} />

        <div
          className={`flex items-center min-w-0 ${
            collapsed ? 'justify-center' : 'gap-2 px-3 py-2'
          }`}
          title={
            collapsed
              ? [userName, ...(assignedRoleNames ?? []).map(roleLabel)].filter(Boolean).join(' · ')
              : undefined
          }
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-shrink-0">
            <User className="h-4 w-4" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate" title={userName}>
                {userName}
              </div>
              {permissionsLoaded && (assignedRoleNames?.length ?? 0) > 0 && (
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate" title={assignedRoleNames?.map(roleLabel).join(', ')}>
                  {[currentRole, ...(assignedRoleNames ?? [])]
                    .filter((r): r is string => typeof r === 'string' && r.length > 0)
                    .filter((r, i, a) => a.indexOf(r) === i)
                    .map(roleLabel)
                    .join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
        {collapsed && (
          <button
            type="button"
            onClick={() => toggleSidebar()}
            className="inline-flex h-10 w-10 items-center justify-center text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-sec)]"
            title="Expand navigation"
            aria-label="Expand navigation"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default Sidebar;


